import { useEffect, useRef } from 'react';
import { drawInstrument, type InstrumentKind } from '@/lib/rack/instruments';

/**
 * 仪器外壳：负责 DPR、尺寸、动画循环和动效偏好降级，绘制交给 instruments.ts。
 * 三台仪器共用这一个壳，所以边框、顶栏、留白在整页是一致的。
 */
export default function Instrument({
  kind,
  label,
  className = '',
}: {
  kind: InstrumentKind;
  label: string;
  className?: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    const DPR = Math.min(devicePixelRatio || 1, 2);
    let W = 0, H = 0, raf = 0;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

    const resize = () => {
      const r = cv.getBoundingClientRect();
      // 尺寸没变就返回：写 canvas.width 会清空画布，而 ResizeObserver 首次回调晚于首帧
      if (r.width === W && r.height === H) return;
      W = r.width; H = r.height;
      cv.width = W * DPR; cv.height = H * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      if (reduced) drawInstrument(kind, ctx, W, H, 3.4);
    };
    const ro = new ResizeObserver(resize);
    ro.observe(cv);
    resize();

    if (reduced) {
      // 静态一帧，取动作中段——不是空白降级
      drawInstrument(kind, ctx, W, H, 3.4);
    } else {
      const loop = (now: number) => {
        drawInstrument(kind, ctx, W, H, now / 1000);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [kind]);

  return (
    <div
      className={`relative rounded-[10px] border border-rack-bone/10 bg-rack-panel overflow-hidden ${className}`}
    >
      <div className="flex items-center gap-2 px-4 py-2.5 bg-rack-chrome border-b border-rack-bone/[0.08]">
        <span className="w-1.5 h-1.5 rounded-full bg-rack-bone/20" />
        <span className="font-readout text-[10px] tracking-[0.18em] text-rack-bone/35 uppercase">
          {label}
        </span>
      </div>
      <canvas ref={ref} className="block w-full h-[268px] sm:h-[300px]" aria-hidden="true" />
    </div>
  );
}
